import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../../contexts/AdminContext";

const SLIDE_COLORS = [
  { tag: "bg-primary", icon: "ri-home-4-line", color: "text-primary", bg: "bg-orange-100" },
  { tag: "bg-secondary", icon: "ri-sofa-line", color: "text-secondary", bg: "bg-emerald-100" },
  { tag: "bg-primary", icon: "ri-sun-line", color: "text-primary", bg: "bg-amber-100" },
  { tag: "bg-rose-500", icon: "ri-restaurant-line", color: "text-rose-500", bg: "bg-rose-100" },
  { tag: "bg-sky-500", icon: "ri-moon-line", color: "text-sky-500", bg: "bg-sky-100" },
];

export default function LifestyleSection() {
  const { settings } = useAdmin();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slides = settings.lifestyleSlides || [];
  const totalSlides = slides.length;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback((idx: number) => {
    if (animating || totalSlides === 0) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 300);
  }, [animating, totalSlides]);

  const prev = useCallback(() => goTo((current - 1 + totalSlides) % totalSlides), [current, goTo, totalSlides]);
  const next = useCallback(() => goTo((current + 1) % totalSlides), [current, goTo, totalSlides]);

  // Auto-play
  useEffect(() => {
    if (totalSlides === 0) return;
    timerRef.current = setTimeout(() => {
      setCurrent((c) => (c + 1) % totalSlides);
    }, 4500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [current, totalSlides]);

  if (totalSlides === 0) return null;

  const slide = slides[current];
  const colors = SLIDE_COLORS[current % SLIDE_COLORS.length];

  return (
    <section ref={ref} className="overflow-hidden" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #fff 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT: Carousel */}
          <div className={`relative transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            {/* Main image container */}
            <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: "4/5", maxHeight: "620px" }}>
              {/* Images */}
              {slides.map((s, i) => (
                <img
                  key={i}
                  src={s.img}
                  alt={s.label}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500"
                  style={{ opacity: i === current ? (animating ? 0 : 1) : 0 }}
                />
              ))}

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

              {/* Top label tag */}
              <div className="absolute top-5 left-5">
                <span className={`${colors.tag} text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full`}>
                  {slide.label}
                </span>
              </div>

              {/* Slide counter */}
              <div className="absolute top-5 right-5 bg-black/40 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                {current + 1} / {slides.length}
              </div>

              {/* Bottom badge */}
              <div className="absolute bottom-20 left-5 right-5">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white inline-flex items-center gap-3 max-w-xs">
                  <div className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className={`${colors.icon} ${colors.color} text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{slide.badgeTitle}</p>
                    <p className="text-slate-400 text-xs">{slide.badgeSub}</p>
                  </div>
                </div>
              </div>

              {/* Navigation arrows */}
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                {/* Dots */}
                <div className="flex items-center gap-1.5">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      className={`rounded-full transition-all duration-300 cursor-pointer ${
                        i === current ? "w-6 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"
                      }`}
                    />
                  ))}
                </div>

                {/* Prev / Next */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={prev}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg"></i>
                  </button>
                  <button
                    onClick={next}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Decorative blobs */}
            <div
              className="absolute -z-10 -bottom-8 -left-8 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(247,57,45,0.08) 0%, transparent 70%)" }}
            />
            <div
              className="absolute -z-10 -top-8 -right-8 w-48 h-48 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(16,185,129,0.07) 0%, transparent 70%)" }}
            />

            {/* Thumbnail strip — hidden on small mobile */}
            <div className="hidden xs:flex sm:flex items-center gap-1.5 sm:gap-2 mt-3 sm:mt-4">
              {slides.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`relative rounded-lg sm:rounded-xl overflow-hidden flex-1 cursor-pointer transition-all duration-300 ${
                    i === current ? "ring-2 ring-offset-1 ring-primary-light opacity-100" : "opacity-50 hover:opacity-80"
                  }`}
                  style={{ height: "44px" }}
                >
                  <img src={s.img} alt={s.label} className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT: text content */}
          <div className={`flex flex-col gap-10 transition-all duration-1000 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="h-px w-6 bg-secondary"></span>
                <span className="uppercase text-secondary-dark text-[10px] font-black tracking-[0.2em]">
                  Stellen Sie sich Ihr Leben morgen vor
                </span>
              </div>
              <h2 className="font-playfair text-4xl md:text-[56px] font-black text-slate-900 leading-[1.1]">
                Mehr als nur ein Los,<br />
                <span className="gradient-text italic font-medium">eine Befreiung</span>
              </h2>
              <p className="text-slate-500 mt-6 leading-relaxed text-lg font-medium">
                Jede Woche ändern gewöhnliche Teilnehmer ihr Schicksal. Eine schuldenfreie Villa, absolute finanzielle Freiheit — Ihr neues Leben beginnt hier.
              </p>
            </div>

            <div className="space-y-6">
              {[
                { 
                  icon: "ri-home-heart-line", 
                  title: "Ihr Traumhaus", 
                  desc: "500 m², Pool, Garten, atemberaubende Aussicht. Ein ganzes Leben, das man in einem außergewöhnlichen Raum gestalten kann.",
                  color: "text-primary",
                  bg: "bg-orange-50"
                },
                { 
                  icon: "ri-shield-star-line", 
                  title: "Sicherheit & finanzielle Freiheit", 
                  desc: `Kein Kredit, keine Miete. Ein Immobilienvermögen von ${settings.lotteryValue}, das auf Ihre Kinder übertragen werden kann.`,
                  color: "text-secondary",
                  bg: "bg-emerald-50"
                },
                { 
                  icon: "ri-sun-line", 
                  title: "Ein Neuanfang", 
                  desc: "Neue Adresse, neuer Alltag. Unsere Gewinner berichten von einem radikal veränderten Leben.",
                  color: "text-primary",
                  bg: "bg-amber-50"
                }
              ].map((point, i) => (
                <div
                  key={i}
                  className="flex items-start gap-5 group"
                >
                  <div className={`w-14 h-14 ${point.bg} rounded-[1.25rem] flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 duration-300 border border-white`}>
                    <i className={`${point.icon} text-2xl ${point.color}`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">{point.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-8 flex">
              <Link
                to="/bien"
                className="w-full group flex items-center justify-center gap-3 bg-slate-900 text-white font-black py-5 rounded-[1.5rem] transition-all duration-300 hover:bg-primary hover:scale-[1.02] shadow-2xl hover:shadow-primary/25"
              >
                ALLE DETAILS ZUR IMMOBILIE ANSEHEN
                <i className="ri-home-4-line text-lg text-primary-light group-hover:text-white transition-colors"></i>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
